import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDashboardAnalytics, getEvents, getCurrentUser } from '../../services/api';
import { Link, useNavigate } from 'react-router-dom';

/**
 * DashboardHome - Vista principal del CRM con estadísticas reales
 */
const DashboardHome = () => {
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName') || 'Organizador';
    const navigate = useNavigate();

    const { data: userData, isLoading: isUserLoading } = useQuery({
        queryKey: ['current-user', userId],
        queryFn: getCurrentUser
    });

    useEffect(() => {
        // Si aún está cargando el usuario, no hacemos nada
        if (isUserLoading || !userData) return;

        if (userData.role === 'organizer') {
            const subStatus = userData.subscription?.status;
            console.log('DashboardHome: current user subscription status:', subStatus);
            
            // Permitimos el acceso si es trialing o active. 
            const isSubscribed = subStatus === 'active' || subStatus === 'trialing';
            
            if (!isSubscribed) {
                // Solo redirigimos si NO es trialing/active Y el status es explícitamente algo que requiere suscripción
                // O si no tiene suscripción en absoluto (usuario nuevo)
                if (!subStatus || subStatus === 'canceled' || subStatus === 'unpaid' || subStatus === 'incomplete') {
                   console.log('DashboardHome: Redirecting to checkout. Reason: invalid status =', subStatus);
                   navigate('/checkout-subscription?plan=organizer&cycle=monthly');
                }
            }
        }
    }, [userData, isUserLoading, navigate]);

    const { data, isLoading } = useQuery({
        queryKey: ['dashboard-analytics', userId],
        queryFn: () => getDashboardAnalytics(userId)
    });

    const analytics = data?.analytics || { totalRevenue: 0, totalPlayers: 0, totalEvents: 0, activeEvents: 0 };

    const stats = [
        { label: 'Recaudación', value: `${analytics.totalRevenue}€`, change: '+12%', icon: '💰', color: 'bg-emerald-500' },
        { label: 'Jugadores', value: analytics.totalPlayers.toString(), change: '+8%', icon: '👥', color: 'bg-primary-500' },
        { label: 'Pozos', value: analytics.totalEvents.toString(), change: '+2', icon: '📝', color: 'bg-amber-500' },
        { label: 'Activos', value: analytics.activeEvents.toString(), change: 'Hoy', icon: '🎾', color: 'bg-purple-500' },
    ];

    const { data: eventsData } = useQuery({
        queryKey: ['my-events-home', userId],
        queryFn: () => getEvents({ organizerId: userId, limit: 3 })
    });

    const upcomingEvents = Array.isArray(eventsData) ? eventsData : (eventsData?.events || []);

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Bienvenida */}
            <div>
                <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-1 sm:mb-2 transition-colors">¡Hola, {userName.split(' ')[0]}! 👋</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">Esto es lo que está pasando en tus pozos hoy.</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                {stats.map((stat, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-800 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all">
                        <div className="flex justify-between items-start mb-2 sm:mb-4">
                            <div className={`${stat.color} p-2 sm:p-3 rounded-lg sm:rounded-xl text-white text-lg sm:text-xl shadow-lg`}>
                                {stat.icon}
                            </div>
                            <span className={`text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ${stat.change.startsWith('+') ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400' : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300'}`}>
                                {stat.change}
                            </span>
                        </div>
                        <h3 className="text-gray-500 dark:text-gray-400 text-[10px] sm:text-sm font-semibold uppercase tracking-wider mb-1">{stat.label}</h3>
                        <p className="text-xl sm:text-3xl font-black text-gray-900 dark:text-white">{isLoading ? '...' : stat.value}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Vista Desktop: Tabla de Pozos Próximos */}
                <div className="xl:col-span-2 bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden transition-all">
                    <div className="p-4 sm:p-6 border-b border-gray-50 dark:border-slate-700 flex justify-between items-center">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Pozos Próximos</h2>
                        <Link to="/dashboard/events" className="text-primary-600 text-sm font-bold hover:underline">Ver todos</Link>
                    </div>

                    {/* Tabla Desktop */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-slate-700/50 text-gray-400 text-xs font-bold uppercase tracking-widest">
                                <tr>
                                    <th className="px-4 py-3">Evento</th>
                                    <th className="px-4 py-3">Fecha</th>
                                    <th className="px-4 py-3">Ocupación</th>
                                    <th className="px-4 py-3">Estado</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50">
                                {upcomingEvents.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-10 text-center text-gray-400 font-bold">No hay eventos próximos</td>
                                    </tr>
                                ) : upcomingEvents.map((event) => (
                                    <tr key={event.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-700/30 transition-colors">
                                        <td className="px-4 py-4">
                                            <div className="font-bold text-gray-900 dark:text-white">{event.title}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{event.club?.name}</div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">{event.date}</div>
                                            <div className="text-xs text-gray-400">
                                                {typeof event.startTime === 'string' ? event.startTime : 'En curso'}h
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-2 bg-gray-100 dark:bg-slate-700 rounded-full max-w-[80px] overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${event.currentPairs === event.maxPairs ? 'bg-red-500' : 'bg-primary-500'}`}
                                                        style={{ width: `${(event.currentPairs / event.maxPairs) * 100}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-xs font-bold text-gray-600 dark:text-gray-400">{Math.round((event.currentPairs / event.maxPairs) * 100)}%</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ${event.status === 'finished' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-600'}`}>
                                                {event.status === 'published' ? 'Abierto' : event.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-right text-xl">
                                            <Link to={`/events/${event.id}/live`} className="text-gray-300 hover:text-primary-600 transition-colors">📺</Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="sm:hidden p-4 space-y-3">
                        {upcomingEvents.length === 0 ? (
                            <p className="text-center text-gray-400 font-bold py-6">No hay eventos próximos</p>
                        ) : upcomingEvents.map((event) => (
                            <div key={event.id} className="border border-gray-100 dark:border-slate-700 rounded-xl p-3 bg-white dark:bg-slate-800">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-gray-900 dark:text-white truncate">{event.title}</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{event.club?.name}</p>
                                    </div>
                                    <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ml-2 ${event.status === 'finished' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-600'}`}>
                                        {event.status === 'published' ? 'Abierto' : event.status}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600 dark:text-gray-400 font-medium">{event.date} · {typeof event.startTime === 'string' ? event.startTime : 'En curso'}h</span>
                                    <Link to={`/events/${event.id}/live`} className="text-primary-600 font-bold">📺 Live</Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actividad Reciente */}
                <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm p-4 sm:p-6 transition-all">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Actividad Reciente</h2>
                    <div className="space-y-4 sm:space-y-6">
                        {[
                            { user: 'Ana M.', action: 'se inscribió en', target: 'Pozo Viernes', time: 'hace 5 min', icon: '👤' },
                            { user: 'Club El As', action: 'actualizó pista', target: 'Pista 4', time: 'hace 12 min', icon: '🏢' },
                            { user: 'Pedro S.', action: 'realizó pago', target: '15€', time: 'hace 45 min', icon: '💳' },
                            { user: 'Sistema', action: 'cerró inscripciones', target: 'Pozo Dom', time: 'hace 1h', icon: '🤖' },
                        ].map((item, idx) => (
                            <div key={idx} className="flex gap-3 items-start">
                                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-gray-50 dark:bg-slate-700/50 flex items-center justify-center text-base sm:text-lg border border-gray-100 dark:border-slate-600 flex-shrink-0">
                                    {item.icon}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-tight">
                                        <span className="font-bold text-gray-900 dark:text-white">{item.user}</span> {item.action} <span className="font-bold text-primary-600">{item.target}</span>
                                    </p>
                                    <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">{item.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="w-full mt-6 py-2 sm:py-3 bg-gray-50 dark:bg-slate-700/30 hover:bg-gray-100 dark:hover:bg-slate-700/50 rounded-lg sm:rounded-xl text-gray-500 dark:text-gray-400 text-xs sm:text-sm font-bold transition-colors">
                        Ver todo el log
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DashboardHome;
